import type { DappDetail } from '../../stores/dappsPageStore';
import { getDappDeletionConstraintMessage } from '../../utils/dappDeletion';
import { Button } from '../ui/button';

interface DeleteDappDialogProps {
  dapp: DappDetail | null;
  deleteReason: string;
  isDeleting: boolean;
  onDeleteReasonChange: (reason: string) => void;
  onClose: () => void;
  onConfirm: () => void;
}

export function DeleteDappDialog({
  dapp,
  deleteReason,
  isDeleting,
  onDeleteReasonChange,
  onClose,
  onConfirm,
}: DeleteDappDialogProps) {
  if (!dapp) {
    return null;
  }

  const deletionBlockedMessage = getDappDeletionConstraintMessage(
    dapp.releaseCount,
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="mx-4 w-full max-w-md rounded-lg bg-neutral-800 p-6">
        <h3 className="mb-4 text-lg font-semibold text-white">Delete Dapp</h3>
        <p className="mb-4 text-sm text-neutral-400">
          Are you sure you want to delete{' '}
          <span className="font-medium text-white">{dapp.dappName}</span>? This
          soft-deletes the dapp and its Portal-owned operational records. Audit,
          review, analytics, and on-chain history are retained. A superadmin can
          restore this deletion unless the package is reused.
        </p>

        {deletionBlockedMessage && (
          <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200">
            {deletionBlockedMessage}
          </div>
        )}

        <div className="mb-4">
          <label
            htmlFor="delete-dapp-reason"
            className="mb-2 block text-sm text-neutral-400"
          >
            Reason for deletion (optional)
          </label>
          <textarea
            id="delete-dapp-reason"
            aria-label="Reason for deletion"
            value={deleteReason}
            onChange={(event) => onDeleteReasonChange(event.target.value)}
            placeholder="Enter reason for deletion..."
            maxLength={500}
            disabled={isDeleting}
            className="w-full rounded-lg border border-neutral-600 bg-neutral-700 px-3 py-2 text-white placeholder-neutral-400 focus:border-teal-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
            rows={3}
          />
        </div>

        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isDeleting}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isDeleting || Boolean(deletionBlockedMessage)}
            className="flex-1 bg-red-600 text-white hover:bg-red-700"
          >
            {isDeleting ? 'Deleting...' : 'Delete Dapp'}
          </Button>
        </div>
      </div>
    </div>
  );
}
